import { useEffect, useId, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { BottomNav } from './BottomNav';
import { SupportFab } from './SupportFab';
import { SplashScreen } from './SplashScreen';
import { LuckyWheelHost } from './lucky-wheel/LuckyWheelSection';
import { AuthModal } from './AuthModal';
import { AuthDialogProvider } from '../auth/AuthDialogContext';
import { useAuth } from '../auth/AuthContext';
import { useTelegramChrome } from '../hooks/useTelegramChrome';
import { applyGeoDefaultsOnce } from '../lib/geoDefaults';

function isRulesPath(pathname: string): boolean {
  return pathname === '/qaydalar' || pathname.startsWith('/qaydalar/') || pathname === '/rules' || pathname.startsWith('/rules/');
}

export function Layout() {
  const { t } = useTranslation('common');
  const { loading, refresh, user } = useAuth();
  const location = useLocation();
  const [wheelOpen, setWheelOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const legalPanelId = useId();
  useTelegramChrome();
  const playOpen = location.pathname.startsWith('/play/');
  const hideChrome = location.pathname.startsWith('/cashier') || playOpen;
  const hideSupportChrome = location.pathname.startsWith('/support');
  const stagingWalletProbe = location.pathname.startsWith('/staging-wallet-connect');
  const hideSiteFooter =
    hideChrome || hideSupportChrome || location.pathname.startsWith('/profile') || stagingWalletProbe || playOpen;
  const hideFab = hideChrome || hideSupportChrome || stagingWalletProbe || playOpen;
  const rulesFooter = isRulesPath(location.pathname);

  useEffect(() => {
    setLegalOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (loading) return;
    void applyGeoDefaultsOnce({
      loggedIn: Boolean(user),
      existingLocale: user?.locale || null,
      existingPlayCurrency: null,
    });
  }, [loading, user?.public_id, user?.locale]);

  return (
    <AuthDialogProvider>
      <div className={`app-shell${hideChrome ? ' cashier-open' : ''}${hideSupportChrome ? ' support-open' : ''}`}>
        <SplashScreen visible={loading && !stagingWalletProbe} onRetry={() => void refresh()} />
        {hideSupportChrome || stagingWalletProbe ? null : <Header onOpenWheel={() => setWheelOpen(true)} />}
        <main>
          <Outlet />
        </main>
        {hideSiteFooter ? null : (
          <footer className={`site-footer${rulesFooter ? ' site-footer--rules' : ''}`}>
            <div className="site-footer-top">
              <strong>VBB PLAY</strong>
              <nav className="site-footer-links" aria-label={t('footerRules')}>
                <Link to="/qaydalar">{t('footerRules')}</Link>
                <Link to="/qaydalar/privacy">{t('footerPrivacy')}</Link>
                <Link to="/support">{t('footerSupport')}</Link>
              </nav>
            </div>

            {rulesFooter ? (
              <div className="site-footer-rules-legal">
                <p className="site-footer-copy">{t('footerRights', { year: 2026 })}</p>
                <div className={`site-footer-accordion${legalOpen ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="site-footer-accordion-trigger"
                    aria-expanded={legalOpen}
                    aria-controls={legalPanelId}
                    onClick={() => setLegalOpen((open) => !open)}
                  >
                    <span>{t('footerLegalAccordionTitle')}</span>
                    <span className="site-footer-accordion-chevron" aria-hidden="true">
                      {legalOpen ? '▴' : '▾'}
                    </span>
                  </button>
                  {legalOpen ? (
                    <section
                      id={legalPanelId}
                      className="site-footer-legal site-footer-legal--accordion"
                      aria-label={t('footerLegalAria')}
                    >
                      <p className="site-footer-legal-statement">{t('footerLegalStatement')}</p>
                    </section>
                  ) : null}
                </div>
              </div>
            ) : (
              <section className="site-footer-legal" aria-label={t('footerLegalAria')}>
                <p className="site-footer-legal-statement">{t('footerLegalStatement')}</p>
              </section>
            )}
          </footer>
        )}
        {hideFab ? null : <SupportFab />}
        {hideChrome || hideSupportChrome || stagingWalletProbe ? null : <BottomNav onOpenWheel={() => setWheelOpen(true)} />}
        <LuckyWheelHost open={wheelOpen} onClose={() => setWheelOpen(false)} />
        <AuthModal />
      </div>
    </AuthDialogProvider>
  );
}
